import React from 'react';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import { compose } from 'redux';
import values from 'lodash/values';
import first from 'lodash/first';
import * as analytics from '@eb/site-analytics';
import { gettext } from '@eb/i18n-ebspa';

import { withRouter } from 'react-router-dom';
import { withMainControls } from '@eb/eds-structure';
import { Tag } from '@eb/eds-tag';
import {
    withEventSaveNotification,
    getCanonicalSearchUrl,
    collections as collectionsDuck,
} from '@eb/destination-utils';

import { handleApplyFilter as handleApplyFilterAction } from '../actions/search';
import { saveEvent as saveEventAction } from '../actions/event';

import {
    eventsCardInfoLookup,
    getSearchContent,
    getSearchSection,
} from '../utils';
import { getTrendingSearchData } from '../utils/trending';
import { hasEventResults } from '../utils/events';
import { addSearchIdToGaAction } from '../utils/analytics';

import TrendingSearchesAnchor from '../components/TrendingSearchesAnchor';
import { Divider } from '@eb/eds-divider';

import { GA_SEARCH_CATEGORY } from '../constants';

const { trackEventFromState, trackEventFromStateOnPageUnload } = analytics;
const {
    actions: {
        addOrRemoveEventToCollection: addOrRemoveEventToCollectionAction,
        createCollection: createCollectionAction,
    },
    selectors: { getFormattedCollections },
} = collectionsDuck;

const SearchNoResultsSuggestionsEvents = loadable(() =>
    import('../components/SearchNoResultsSuggestionsEvents'),
);
const LazyLowResultsBanner = loadable(
    () => import('../components/EmptyState'),
    {
        resolveComponent: (components) => components.LowResults,
    },
);
const LazyNoResultsBanner = loadable(() => import('../components/EmptyState'), {
    resolveComponent: (components) => components.NoResults,
});

import './SearchResultsPivotsContainer.scss';

const EventSuggestions = ({
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
    ...props
}) => {
    return (
        <section className="pivot-section">
            <SearchNoResultsSuggestionsEvents
                userCollections={userCollections}
                onAddOrRemoveEventToCollection={onAddOrRemoveEventToCollection}
                onCreateCollection={onCreateCollection}
                {...props}
            />
        </section>
    );
};

const TagSuggestions = (props) => {
    const handleTagClick = (val) => {
        window.scroll(0, 0);
        props.handleApplyFilter({ q: `#${val}` });

        //TODO: add analytics call here
    };

    if (props.suggestedTags.length < 3) {
        return null;
    }

    return (
        <section className="pivot-section suggested-tags">
            <div className="eds-l-pad-left-2 eds-l-pad-bot-8">
                <h3 className="eds-text-hs eds-text-color--grey-700">
                    {gettext('Popular tags')}
                </h3>
            </div>
            <section className="suggested-tags__tags">
                {props.suggestedTags.map((tag) => (
                    <div className="eds-l-mar-all-1" key={tag.value}>
                        <Tag
                            style="outlined"
                            onActiveToggle={() => handleTagClick(tag.value)}
                        >
                            #{tag.display}
                        </Tag>
                    </div>
                ))}
            </section>
        </section>
    );
};

const TrendingSearchesLink = (props) => {
    return (
        <>
            <div className="eds-l-pad-top-6 eds-l-pad-bot-10">
                <Divider color="ui-200" />
            </div>
            <TrendingSearchesAnchor
                trendingSearchData={props.trendingSearchData}
            />
        </>
    );
};

/**
 * Suggested actions for the user to take given low, empty, or
 * end of search results. Additionally always displays the
 * trending searches footer.
 *
 * @param {*} props
 */
export const SearchResultsPivots = (props) => {
    const { isLoading, hasEventResults, isNextDisabled, ...otherProps } = props;

    const shouldRenderSuggestions =
        !isLoading && (!hasEventResults || isNextDisabled);

    return (
        <div className="search-result-pivots">
            {shouldRenderSuggestions && (
                <>
                    <section className="search-result-pivots__empty-state">
                        {hasEventResults ? (
                            <LazyLowResultsBanner containerClassname="eds-l-pad-hor-6" />
                        ) : (
                            <LazyNoResultsBanner containerClassname="eds-l-pad-hor-6" />
                        )}
                    </section>
                    <EventSuggestions {...otherProps} />
                    <TagSuggestions {...otherProps} />
                </>
            )}
            <TrendingSearchesLink
                trendingSearchData={props.trendingSearchData}
            />
        </div>
    );
};

const _getEventSuggestions = (rawEvents, currencyFormat, section) => {
    const events = values(rawEvents);

    return eventsCardInfoLookup({
        bucketsContent: events,
        currencyFormat,
        section,
    });
};

const _mapStateToProps = (state) => {
    const section = getSearchSection(state.location.isOnline);
    const results = getSearchContent(
        state.content.entities,
        state.pagination,
        state.currencyFormat,
        section,
    );
    const _getMaxPage = ({ event }) => {
        const firstKey = first(Object.keys(event));

        let pageCount;

        if (event[firstKey]) {
            pageCount = event[firstKey].pageCount;
        }

        return pageCount;
    };
    const _isNextDisabled = (pagination) => {
        const pageCount = _getMaxPage(pagination);
        const { currentPage } = pagination;

        return currentPage === pageCount - 1;
    };

    return {
        isNextDisabled: _isNextDisabled(state.pagination),
        hasEventResults: hasEventResults(results),
        userCollections: getFormattedCollections(state),
        featureFlags: state.app.featureFlags,
        suggestionsLoading: state.suggestions.loading,
        eventSuggestions: _getEventSuggestions(
            state.suggestions.rawEvents,
            state.currencyFormat,
            section,
        ),
        suggestedTags: state.suggestions.tags,
        isAuthenticated: state.auth.isAuthenticated,
        shareOptions: state.share.options,
        allEventsUrl: getCanonicalSearchUrl({ slug: state.location.slug }),
        trackGAEvent: (action) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
        trendingSearchData: getTrendingSearchData(
            state.location,
            state.app.domain,
            state.shouldShowTrendingSearchPageLink,
        ),
        trackGAEventOnPageUnload: (action) =>
            trackEventFromStateOnPageUnload(
                state,
                addSearchIdToGaAction(
                    {
                        ...action,
                        category: GA_SEARCH_CATEGORY,
                    },
                    state,
                ),
            ),
    };
};

const _mapDispatchToProps = (dispatch, ownProps) => ({
    onSaveEvent: (...args) => dispatch(saveEventAction(...args, true)),
    onAddOrRemoveEventToCollection: (...args) =>
        dispatch(
            addOrRemoveEventToCollectionAction(GA_SEARCH_CATEGORY)(...args),
        ),
    onCreateCollection: (...args) =>
        dispatch(createCollectionAction(GA_SEARCH_CATEGORY)(...args)),
    handleApplyFilter: (...args) =>
        dispatch(handleApplyFilterAction(ownProps.history, ...args)),
});

const enhance = compose(
    withMainControls,
    withRouter,
    connect(_mapStateToProps, _mapDispatchToProps),
    withEventSaveNotification,
);

export default enhance(SearchResultsPivots);
